import { FC, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import type { BlacklistDraftRow } from "../hooks/use-blacklist-draft";
import { foreignOwnerName } from "~/shared/lib/holding-type";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Truck types not already on this location's list. */
  truckTypes: ConnectProto.CustomTruckType[];
  /** The location's provider: a type another company of its holding made names that company. */
  companyId?: bigint | number | string;
  onAdd: (truckTypeId: bigint, truckTypeName: string, reason: string) => void;
  row?: BlacklistDraftRow | null;
  onRemove?: (key: string) => void;
};

/**
 * Adds one truck type to the pickup location's blacklist.
 *
 * The entry is handed to the caller's draft, not written here — the blacklist
 * tab stages its edits until the location modal is saved.
 */
export const BlacklistModal: FC<Props> = ({
  isOpen,
  onClose,
  truckTypes,
  companyId,
  onAdd,
  row,
  onRemove,
}) => {
  const t = useT();
  const [truckTypeId, setTruckTypeId] = useState("");
  const [reason, setReason] = useState("");
  const isExisting = Boolean(row);

  // Reopening the dialog must not offer to re-add whatever was picked last time.
  useEffect(() => {
    if (isOpen && !row) {
      setTruckTypeId("");
      setReason("");
    }
  }, [isOpen, row]);

  const handleAdd = () => {
    const truckType = truckTypes.find((type) => String(type.id) === truckTypeId);
    if (!truckType) return;
    onAdd(truckType.id, truckType.name, reason.trim());
    onClose();
  };

  const handleRemove = () => {
    if (!row) return;
    onRemove?.(row.key);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      {/* DialogContent sets no width of its own — only a max — so without an
          explicit one it shrinks to fit its content and the fields end up
          cramped. Its own p-6/gap-4 need `!` to lose: this dialog draws its own
          sections and separators, and the inherited gap would add 64px of dead
          space between them. */}
      <DialogContent className="w-[480px] max-w-[96vw] overflow-hidden !gap-0 !p-0">
        <DialogHeader className="!p-4">
          <DialogTitle className="text-xl font-bold">
            {t(
              Namespace.PickupLocation,
              isExisting ? "blacklist.modal.editTitle" : "blacklist.modal.title",
            )}
          </DialogTitle>
        </DialogHeader>
        <Separator />

        <div className="space-y-4 p-4">
          <div className="flex items-center justify-between gap-4">
            <Label className="whitespace-nowrap text-sm font-normal">
              {t(Namespace.PickupLocation, "blacklist.truckType")}
            </Label>
            {isExisting ? (
              <div className="flex h-9 w-[232px] items-center rounded-lg border border-input bg-muted px-3 text-sm text-muted-foreground">
                {row?.truckTypeName}
              </div>
            ) : (
              <Select value={truckTypeId} onValueChange={setTruckTypeId}>
                <SelectTrigger className="h-9 w-[232px] bg-white">
                  <SelectValue
                    placeholder={t(Namespace.PickupLocation, "blacklist.modal.selectPlaceholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {truckTypes.length === 0 ? (
                    // Without this the dropdown opens as a blank sliver and the
                    // dispatcher has no idea why nothing is on offer.
                    <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                      {t(Namespace.PickupLocation, "blacklist.modal.noTruckTypes")}
                    </div>
                  ) : (
                    truckTypes.map((type) => {
                      const owner = foreignOwnerName(type, companyId);
                      return (
                        <SelectItem key={String(type.id)} value={String(type.id)}>
                          {type.name}
                          {owner && (
                            <span className="ml-2 text-xs text-text-secondary">{owner}</span>
                          )}
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="blacklist-reason" className="block text-sm font-normal leading-5">
              {t(Namespace.PickupLocation, "blacklist.modal.reasonLabel")}
            </Label>
            {isExisting ? (
              <div className="h-[67px] w-full overflow-y-auto whitespace-pre-wrap rounded-lg border border-input bg-muted px-3 py-2 text-sm text-muted-foreground">
                {row?.reason || "—"}
              </div>
            ) : (
              <textarea
                id="blacklist-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t(Namespace.PickupLocation, "blacklist.reasonPlaceholder")}
                className="h-[67px] w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
            )}
          </div>
        </div>

        <Separator />
        {/* Removal lives here rather than on the table row: the row only shows
            the entry, this dialog is where it is acted on. */}
        <div className="flex items-center justify-between gap-4 p-4">
          {isExisting ? (
            <Button type="button" variant="solidRed" onClick={handleRemove}>
              {t(Namespace.Common, "delete")}
            </Button>
          ) : (
            <>
              <span />
              <Button type="button" onClick={handleAdd} disabled={!truckTypeId}>
                {t(Namespace.PickupLocation, "blacklist.modal.submit")}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
